import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { LocalDate } from "@js-joda/core";
import { SalaryType } from "model/Salary";
import { useTranslation } from "react-i18next";
import type {
  StaffProfileData,
  StaffProfileEmploymentCurrent,
} from "services/api/company/staff.service";
import { formatDateLabelInStockholm } from "web-apps/company/utils/timeAndDate";
import type {
  EmploymentDetailsForm,
  EmploymentOptions,
  EmploymentUiRules,
} from "../../profilePanel.contract";
import {
  FieldCard,
  FieldLabel,
  FieldsGrid,
  FieldValue,
  NoEmploymentBanner,
  NoEmploymentBannerBody,
  UpcomingEmploymentBanner,
  UpcomingEmploymentBannerBody,
  Section,
  SectionActionRow,
  SectionBox,
  SectionTitle,
} from "../../layout/profilePanel.styled";

interface ProfilePanelEmploymentSectionProps {
  isEditMode: boolean;
  isCreateMode: boolean;
  editedEmploymentDetails: EmploymentDetailsForm;
  profile: StaffProfileData;
  currentEmployment: StaffProfileEmploymentCurrent | null;
  upcomingEmploymentStartDate: string | null;
  showNoEmploymentBanner: boolean;
  employmentRules: EmploymentUiRules;
  employmentOptions: EmploymentOptions;
  onEmploymentFieldChange: <TField extends keyof EmploymentDetailsForm>(
    field: TField,
    value: EmploymentDetailsForm[TField],
  ) => void;
  isLoadingEmployeeNumber: boolean;
  onFetchEmployeeNumber: () => void;
  onChangeSalaryClick: () => void;
  onChangeTaxClick: () => void;
}

const twoColumnGridStyle = {
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
} as const;

const toMandatoryLabel = (
  label: string,
  translate: (key: string) => string,
): string => `${label} (${translate("Staff.AddStaffPanelMandatory")})`;

/**
 * Handles profile panel employment section.
 */
export const ProfilePanelEmploymentSection = (
  props: ProfilePanelEmploymentSectionProps,
): JSX.Element => {
  const { t } = useTranslation();
  const salaryTypeInForm = props.isEditMode
    ? props.editedEmploymentDetails.salaryType
    : props.currentEmployment?.salaryType;
  const shouldShowWorkPercentage = salaryTypeInForm === SalaryType.Monthly;
  const hasUpcomingEmployment = props.upcomingEmploymentStartDate != null;

  return (
    <>
      <Section>
        <SectionTitle>{t("Staff.ProfilePanelEmployment")}</SectionTitle>
        {props.upcomingEmploymentStartDate != null && (
          <UpcomingEmploymentBanner>
            <UpcomingEmploymentBannerBody>
              {t("Staff.ProfilePanelUpcomingEmploymentStartsFrom", {
                date: formatDateLabelInStockholm(
                  props.upcomingEmploymentStartDate,
                  "sv-SE",
                ),
              })}
            </UpcomingEmploymentBannerBody>
          </UpcomingEmploymentBanner>
        )}
        {!hasUpcomingEmployment && props.showNoEmploymentBanner && (
          <NoEmploymentBanner>
            <NoEmploymentBannerBody>
              {t("Staff.ProfilePanelNoActiveOrUpcomingEmployment")}
            </NoEmploymentBannerBody>
          </NoEmploymentBanner>
        )}
        <SectionBox>
          <FieldsGrid style={twoColumnGridStyle}>
            <FieldCard>
              {props.isEditMode ? (
                <Dropdown
                  fullWidth
                  label={toMandatoryLabel(t("Staff.EmploymentType"), t)}
                  options={props.employmentOptions.employmentOptions}
                  value={props.editedEmploymentDetails.employmentType}
                  onChange={(employmentType: string) =>
                    props.onEmploymentFieldChange(
                      "employmentType",
                      employmentType,
                    )
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.employmentType
                      ? t(
                          `Staff.employmentType${props.currentEmployment.employmentType}`,
                        )
                      : "-"}
                  </FieldValue>
                  <FieldLabel>{t("Staff.EmploymentType")}</FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <Dropdown
                  fullWidth
                  label={toMandatoryLabel(
                    t("OrganizationalStructure.EmployeeCategory"),
                    t,
                  )}
                  options={props.employmentOptions.employeeCategoryOptions}
                  value={props.editedEmploymentDetails.employeeCategory}
                  onChange={(employeeCategory: string) =>
                    props.onEmploymentFieldChange(
                      "employeeCategory",
                      employeeCategory,
                    )
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.employeeCategory &&
                    props.currentEmployment.employeeCategory !== "Undefined"
                      ? (props.employmentOptions.employeeCategoryLabelByValue[
                          props.currentEmployment.employeeCategory
                        ] ?? props.currentEmployment.employeeCategory)
                      : "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldStaffType")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  type="number"
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldEmployeeNumber"),
                    t,
                  )}
                  value={props.editedEmploymentDetails.employeeNumber}
                  iconName="v2/reload"
                  iconSize="18px"
                  iconAriaLabel={t(
                    "Staff.AddStaffPanelActionReloadEmployeeNumber",
                  )}
                  onIconClick={props.onFetchEmployeeNumber}
                  disabled={props.isLoadingEmployeeNumber}
                  onChange={(employeeNumber) =>
                    props.onEmploymentFieldChange(
                      "employeeNumber",
                      employeeNumber,
                    )
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.employeeNumber ?? "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldEmployeeNumber")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={toMandatoryLabel(t("Staff.AddStaffPanelFieldRole"), t)}
                  value={props.editedEmploymentDetails.role}
                  onChange={(role) =>
                    props.onEmploymentFieldChange("role", role)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.role?.trim() || "-"}
                  </FieldValue>
                  <FieldLabel>{t("Staff.AddStaffPanelFieldRole")}</FieldLabel>
                </>
              )}
            </FieldCard>
            {shouldShowWorkPercentage && (
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    type="number"
                    label={toMandatoryLabel(
                      `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`,
                      t,
                    )}
                    value={props.editedEmploymentDetails.workPercentage}
                    onChange={(workPercentage) =>
                      props.onEmploymentFieldChange(
                        "workPercentage",
                        workPercentage,
                      )
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.profile.workPercentage == null
                        ? "-"
                        : `${Math.round(props.profile.workPercentage * 100)}%`}
                    </FieldValue>
                    <FieldLabel>
                      {`${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            )}
            <FieldCard>
              {props.isEditMode ? (
                <DatePicker
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldStartDate"),
                    t,
                  )}
                  value={props.editedEmploymentDetails.startDate}
                  allowClear={false}
                  onChange={(startDate: LocalDate | null) =>
                    props.onEmploymentFieldChange("startDate", startDate)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatDateLabelInStockholm(
                      props.currentEmployment?.startDate ?? "",
                      "sv-SE",
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldStartDate")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <DatePicker
                  label={
                    props.employmentRules.isEndDateRequiredOnSave
                      ? toMandatoryLabel(
                          t("Staff.AddStaffPanelFieldEndDate"),
                          t,
                        )
                      : t("Staff.AddStaffPanelFieldEndDate")
                  }
                  value={props.editedEmploymentDetails.endDate}
                  onChange={(endDate: LocalDate | null) =>
                    props.onEmploymentFieldChange("endDate", endDate)
                  }
                  allowClear={
                    !props.employmentRules.isProjectEmployee &&
                    !props.employmentRules.isTraineeEmployee
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatDateLabelInStockholm(
                      props.currentEmployment?.endDate ?? "",
                      "sv-SE",
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldEndDate")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
          </FieldsGrid>
        </SectionBox>
      </Section>

      <Section>
        <SectionTitle>{t("Staff.ProfilePanelSalary")}</SectionTitle>
        <SectionBox>
          <FieldsGrid style={twoColumnGridStyle}>
            <FieldCard>
              {props.isEditMode && props.isCreateMode ? (
                <Dropdown
                  fullWidth
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldSalaryModel"),
                    t,
                  )}
                  options={props.employmentOptions.salaryOptions}
                  value={props.editedEmploymentDetails.salaryType}
                  onChange={(salaryType: string) =>
                    props.onEmploymentFieldChange("salaryType", salaryType)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.salaryType
                      ? t(
                          `Staff.salaryType${props.currentEmployment.salaryType}`,
                        )
                      : "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldSalaryModel")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode && props.isCreateMode ? (
                <InputField
                  fullWidth
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldSalaryAmount"),
                    t,
                  )}
                  value={props.editedEmploymentDetails.salary}
                  onChange={(salary) =>
                    props.onEmploymentFieldChange("salary", salary)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.salary == null
                      ? "-"
                      : (() => {
                          const displayValue = String(
                            props.currentEmployment.salary,
                          ).trim();
                          return displayValue.length > 0
                            ? `${displayValue} kr`
                            : "-";
                        })()}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldSalaryAmount")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode && props.isCreateMode ? (
                <Dropdown
                  fullWidth
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldPaymentSolution"),
                    t,
                  )}
                  options={props.employmentOptions.paymentOptions}
                  value={props.editedEmploymentDetails.paymentType}
                  onChange={(paymentType: string) =>
                    props.onEmploymentFieldChange("paymentType", paymentType)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.paymentType
                      ? props.currentEmployment.paymentType === "None"
                        ? t("System.OwnSalaryManagement")
                        : "SimflexityPay"
                      : "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldPaymentSolution")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
          </FieldsGrid>
          {!props.isEditMode && (
            <SectionActionRow>
              <SecondaryButton
                label={t("Staff.ProfilePanelChangeSalary")}
                size="small"
                onClick={props.onChangeSalaryClick}
              />
            </SectionActionRow>
          )}
        </SectionBox>
      </Section>

      <Section>
        <SectionTitle>{t("Salary.TaxProcent")}</SectionTitle>
        <SectionBox>
          <FieldsGrid style={twoColumnGridStyle}>
            <FieldCard>
              <>
                <FieldValue>
                  {props.currentEmployment?.taxTable ?? "-"}
                </FieldValue>
                <FieldLabel>{t("Staff.AddStaffPanelFieldTaxTable")}</FieldLabel>
              </>
            </FieldCard>
          </FieldsGrid>
          {!props.isEditMode && (
            <SectionActionRow>
              <SecondaryButton
                label={t("Staff.ProfilePanelChangeTax")}
                size="small"
                onClick={props.onChangeTaxClick}
              />
            </SectionActionRow>
          )}
        </SectionBox>
      </Section>

      <Section>
        <SectionTitle>{t("Staff.ProfilePanelContractTerms")}</SectionTitle>
        <SectionBox>
          <FieldsGrid style={twoColumnGridStyle}>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldCollectiveSalaryAgreement")}
                  value={props.editedEmploymentDetails.agreement}
                  onChange={(agreement) =>
                    props.onEmploymentFieldChange("agreement", agreement)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.agreement?.trim() || "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldCollectiveSalaryAgreement")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  type="number"
                  label={t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                  value={props.editedEmploymentDetails.noticeDays}
                  onChange={(noticeDays) =>
                    props.onEmploymentFieldChange("noticeDays", noticeDays)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.noticeDays ?? "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  type="number"
                  label={t("Staff.AddStaffPanelFieldVacationDays")}
                  value={props.editedEmploymentDetails.vacationDays}
                  onChange={(vacationDays) =>
                    props.onEmploymentFieldChange("vacationDays", vacationDays)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.vacationDays ?? "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldVacationDays")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldWorkPlace")}
                  value={props.editedEmploymentDetails.workplace}
                  onChange={(workplace) =>
                    props.onEmploymentFieldChange("workplace", workplace)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.workplace?.trim() || "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldWorkPlace")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
          </FieldsGrid>
        </SectionBox>
      </Section>
    </>
  );
};
